import React from 'react';
import { Stack, Typography, Checkbox, FormControlLabel } from '@mui/material';
import { Button, Loader, TextField } from '@orijinworks/frontend-commons';
import { useParams } from 'react-router-dom';
import FormFormioBuilder from './FormFormioBuilder';
import { useFormManageHook } from './useFormManageHook';
import { NavyMainTypography } from '../modules/Feeds/Feeds.style';

/**
 * Form management screen — create (`/forms`) or edit (`/forms/:formId`).
 * API: portal `POST/PUT /api/content/form` with `name`, `schema`, `is_active`.
 * `schema` stores a Form.io form JSON (`display`, `components`, …).
 */
const FormManage = React.memo(() => {
  const { formId } = useParams();
  const {
    formName,
    isActive,
    builderInitialForm,
    schemaEditorKey,
    nameError,
    pageLoading,
    isBusy,
    isEditMode,
    handleFormNameChange,
    handleIsActiveChange,
    handleFormSchemaChange,
    handleSubmit,
    handleBackToFeeds,
  } = useFormManageHook();

  return (
    <>
      <Loader isLoading={isBusy} />
      <Stack
        component="form"
        onSubmit={handleSubmit}
        height="100%"
        minHeight="100%"
        backgroundColor="white"
        padding="24px"
        spacing={3}
        maxWidth={1400}
        width="100%"
        margin="0 auto"
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <NavyMainTypography variant="h1">
            {isEditMode ? 'Edit form' : 'Create form'}
          </NavyMainTypography>
          <Button type="button" variant="text" onClick={handleBackToFeeds} disabled={isBusy}>
            Back to Feeds
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {isEditMode
            ? 'Update the form name, active state, and fields below. The saved schema is Form.io form JSON.'
            : 'Form name is required. Build the form below; the saved schema is Form.io form JSON.'}
        </Typography>
        <TextField
          fullWidth
          required
          label="Form name"
          name="formName"
          value={formName}
          onChange={handleFormNameChange}
          error={Boolean(nameError)}
          helperText={nameError || ' '}
          disabled={pageLoading}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={isActive}
              onChange={handleIsActiveChange}
              color="primary"
              disabled={pageLoading}
            />
          }
          label="Is active"
        />
        <Typography variant="subtitle2" color="text.primary" sx={{ pt: 1 }}>
          Form builder
        </Typography>
        <FormFormioBuilder
          key={`${formId ?? 'new'}-${schemaEditorKey}`}
          initialForm={builderInitialForm}
          onChange={handleFormSchemaChange}
          disabled={pageLoading}
        />
        <Button type="submit" variant="contained" color="primary" disabled={isBusy}>
          Submit
        </Button>
      </Stack>
    </>
  );
});

export default FormManage;
